var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Text } from '../../../atoms/Text/Text';
import { Button } from '../../../atoms/Button/Button';
import { Loader } from '../../../atoms/Loader';
import { AttachmentModal } from '../../../molecules/AttachmentModal';
import { SUPPORT_ATTACHMENT_DISPOSITION, SUPPORT_COPY } from '../../../../features/AIChatWidget/constants/support';
import { resolveAttachmentDownloadMode } from '../../../../features/AIChatWidget/helpers/attachmentDownload';
import { attachmentDetails, attachmentIconClass, attachmentKind, formatFileSize } from '../attachmentFormat';
import { useAttachmentScan } from '../useAttachmentScan';
import './SupportAttachmentStyles.scss';
const baseClass = 'qmb-ui-ai-support-attachment';
let triggerIdCounter = 0;
function nextTriggerId() {
    triggerIdCounter += 1;
    return `${baseClass}-trigger-${triggerIdCounter}`;
}
function metaText(attachment, scan) {
    if (scan === 'scanning')
        return SUPPORT_COPY.ATTACHMENT_SCANNING;
    if (scan === 'unchecked')
        return SUPPORT_COPY.ATTACHMENT_UNCHECKED;
    return formatFileSize(attachment.sizeBytes);
}
function FileChip({ attachment, scan = 'ready', triggerId, onClick }) {
    const meta = metaText(attachment, scan);
    const interactive = onClick !== undefined;
    const content = (React.createElement(React.Fragment, null,
        React.createElement("i", { className: `${baseClass}__icon ${attachmentIconClass(attachment)}`, "aria-hidden": "true" }),
        React.createElement("span", { className: `${baseClass}__text` },
            React.createElement(Text, { variant: "x-small", className: `${baseClass}__name` }, attachment.name),
            meta && (React.createElement(Text, { variant: "xx-small", className: `${baseClass}__meta${scan === 'unchecked' ? ` ${baseClass}__error-text` : ''}` }, meta)))));
    const classes = [
        baseClass,
        interactive ? `${baseClass}--interactive` : '',
        scan === 'unchecked' ? `${baseClass}--error` : ''
    ]
        .filter(Boolean)
        .join(' ');
    if (!interactive)
        return React.createElement("div", { className: classes }, content);
    return (React.createElement("button", { type: "button", id: triggerId, className: classes, onClick: onClick }, content));
}
/**
 * A file on either side of the support conversation. Every one opens the same in-widget modal:
 * an image previews, a pdf embeds (with a "Can't preview? Download" line), anything else shows a
 * file card, and all of them have a Download link (started the way this environment needs, see
 * helpers/attachmentDownload). Nothing here opens a window: an iOS host may load a new window
 * inside the chat itself, replacing it.
 *
 * Images also show an inline thumbnail, loaded when they come into view.
 *
 * URLs are never kept anywhere durable and never built client-side: the resolver hands back an
 * authorized URL each time (a preview URL, and separately the Download URL, which the server
 * answers with `Content-Disposition: attachment`), and an expired one is simply asked for again.
 * A file that has not been scanned clean is shown but never fetched or opened: "Scanning…" while
 * the scan is pending (re-checked for a couple of minutes), and "This file couldn't be checked"
 * when it failed, found something, or never finished. The attachment route's own 409s
 * (SCAN_PENDING / SCAN_FAILED) land in the same two states.
 */
export function SupportAttachment({ attachment, resolveUrl }) {
    const kind = attachmentKind(attachment);
    const isImage = kind === 'image';
    const { state: scan, absorb: absorbScanError } = useAttachmentScan(attachment, resolveUrl);
    const canFetch = Boolean(resolveUrl) && scan === 'ready';
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewPhase, setPreviewPhase] = useState(isImage && canFetch ? 'loading' : 'idle');
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [downloadFailed, setDownloadFailed] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    // How Download starts in this environment (B47): decided once, the environment does not change.
    const [downloadMode] = useState(resolveAttachmentDownloadMode);
    const refreshedRef = useRef(false);
    const mountedRef = useRef(true);
    const triggerIdRef = useRef(nextTriggerId());
    useEffect(() => () => {
        mountedRef.current = false;
    }, []);
    const loadPreview = useCallback((forceRefresh) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!resolveUrl)
            return;
        setPreviewPhase('loading');
        try {
            const { url } = yield resolveUrl(attachment.id, {
                forceRefresh,
                disposition: SUPPORT_ATTACHMENT_DISPOSITION.INLINE,
                source: (_a = attachment.source) !== null && _a !== void 0 ? _a : 'support'
            });
            if (!mountedRef.current)
                return;
            setPreviewUrl(url);
            setPreviewPhase('idle');
        }
        catch (error) {
            if (!mountedRef.current)
                return;
            if (absorbScanError(error))
                setModalOpen(false); // the server says it is not clean: never open it
            else
                setPreviewPhase('error');
        }
    }), [attachment.id, attachment.source, resolveUrl, absorbScanError]);
    const loadDownload = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!resolveUrl)
            return;
        setDownloadFailed(false);
        try {
            const { url } = yield resolveUrl(attachment.id, {
                disposition: SUPPORT_ATTACHMENT_DISPOSITION.ATTACHMENT,
                source: (_a = attachment.source) !== null && _a !== void 0 ? _a : 'support'
            });
            if (mountedRef.current)
                setDownloadUrl(url);
        }
        catch (error) {
            if (!mountedRef.current)
                return;
            if (absorbScanError(error))
                setModalOpen(false);
            else
                setDownloadFailed(true);
        }
    }), [attachment.id, attachment.source, resolveUrl, absorbScanError]);
    // The inline thumbnail of an image loads on mount.
    useEffect(() => {
        if (isImage && canFetch)
            void loadPreview(false);
    }, [isImage, canFetch, loadPreview]);
    // Opening the modal prepares what it needs: the Download link always, and a pdf's preview. The
    // resolver reuses a URL that still has time left, and refetches one that is about to expire.
    useEffect(() => {
        if (!modalOpen || !canFetch)
            return;
        void loadDownload();
        if (kind === 'pdf' && !previewUrl)
            void loadPreview(false);
    }, [modalOpen]);
    // The short-lived URL expired between fetch and paint: ask for a fresh one, once. Shared by the
    // thumbnail and the modal's preview, which show the same URL.
    const handleImageError = useCallback(() => {
        if (refreshedRef.current) {
            setPreviewPhase('error');
            return;
        }
        refreshedRef.current = true;
        void loadPreview(true);
    }, [loadPreview]);
    const handleRetryPreview = useCallback(() => {
        refreshedRef.current = false;
        void loadPreview(true);
    }, [loadPreview]);
    const open = useCallback(() => setModalOpen(true), []);
    const close = useCallback(() => setModalOpen(false), []);
    if (!canFetch) {
        return React.createElement(FileChip, { attachment: attachment, scan: scan });
    }
    // An image whose thumbnail could not load, with the modal closed: say so, and offer a retry.
    if (isImage && previewPhase === 'error' && !modalOpen) {
        return (React.createElement("div", { className: `${baseClass} ${baseClass}--error` },
            React.createElement("i", { className: `${baseClass}__icon fa-light fa-image`, "aria-hidden": "true" }),
            React.createElement("span", { className: `${baseClass}__text` },
                React.createElement(Text, { variant: "x-small", className: `${baseClass}__name` }, attachment.name),
                React.createElement(Text, { variant: "xx-small", className: `${baseClass}__error-text`, role: "alert" }, SUPPORT_COPY.ATTACHMENT_ERROR)),
            React.createElement(Button, { className: `${baseClass}__retry`, onClick: handleRetryPreview }, SUPPORT_COPY.ATTACHMENT_RETRY)));
    }
    return (React.createElement(React.Fragment, null,
        isImage ? (React.createElement("button", { type: "button", id: triggerIdRef.current, className: `${baseClass} ${baseClass}--image ${baseClass}--interactive`, "aria-label": `Open ${attachment.name}`, onClick: open, disabled: !previewUrl }, previewUrl ? (React.createElement("img", { className: `${baseClass}__thumbnail`, src: previewUrl, alt: attachment.name, loading: "lazy", onError: handleImageError })) : (React.createElement(Loader, { size: "24", title: SUPPORT_COPY.ATTACHMENT_LOADING })))) : (React.createElement(FileChip, { attachment: attachment, triggerId: triggerIdRef.current, onClick: open })),
        React.createElement(AttachmentModal, { isOpen: modalOpen, onClose: close, title: attachment.name, kind: kind, previewUrl: previewUrl, downloadUrl: downloadUrl, downloadMode: downloadMode, isPreviewLoading: kind !== 'file' && previewPhase === 'loading', errorMessage: kind !== 'file' && previewPhase === 'error' ? SUPPORT_COPY.ATTACHMENT_ERROR : undefined, retryLabel: SUPPORT_COPY.ATTACHMENT_RETRY, onRetry: handleRetryPreview, onImageError: handleImageError, fileIconClass: attachmentIconClass(attachment), fileDetails: attachmentDetails(attachment), notice: downloadFailed ? SUPPORT_COPY.ATTACHMENT_DOWNLOAD_FAILED : undefined, downloadLabel: SUPPORT_COPY.ATTACHMENT_DOWNLOAD, previewFallbackLabel: SUPPORT_COPY.ATTACHMENT_PREVIEW_FALLBACK, loadingLabel: SUPPORT_COPY.ATTACHMENT_LOADING, closeLabel: SUPPORT_COPY.ATTACHMENT_LIGHTBOX_CLOSE, triggerElementId: triggerIdRef.current })));
}
